import styled, { keyframes, css } from "styled-components";

const shimmer = keyframes`
  0%   { background-position: -800px 0; }
  100% { background-position:  800px 0; }
`;

/* ─── shared shimmer mixin via css helper ─── */
const shimmerDark = css`
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.07) 25%,
    rgba(0, 0, 0, 0.13) 37%,
    rgba(0, 0, 0, 0.07) 63%
  );
  background-size: 800px 100%;
  animation: ${shimmer} 1.5s ease infinite;
`;

const shimmerLight = css`
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.15) 25%,
    rgba(255, 255, 255, 0.30) 37%,
    rgba(255, 255, 255, 0.15) 63%
  );
  background-size: 800px 100%;
  animation: ${shimmer} 1.5s ease infinite;
`;

/**
 * Base shimmer element — dark variant (for light backgrounds).
 */
export const SkeletonPulse = styled.div`
  ${shimmerDark}
  display: block;
  flex-shrink: 0;
`;

/**
 * Light shimmer element — for dark backgrounds (hero, footer).
 */
export const SkeletonPulseLight = styled.div`
  ${shimmerLight}
  display: block;
  flex-shrink: 0;
`;

/* ═══════════════════════════════════════════
   HEADER  (white bar, fixed)
═══════════════════════════════════════════ */

export const HeaderSkeletonWrapper = styled.div`
  width: 100%;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0;
  box-sizing: border-box;
  background: #fff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  position: fixed;
  top: 0;
  left: 0;
  z-index: 510;
  overflow: hidden;
`;

/** Left logo block — wide grey rectangle like a logo image */
export const SkeletonLogoBlock = styled.div`
  ${shimmerDark}
  width: 150px;
  height: 100%;
  border-radius: 0;
  flex-shrink: 0;
`;

/** Right actions cluster */
export const SkeletonHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding-right: 20px;
`;

/* ═══════════════════════════════════════════
   PAGE CONTENT
═══════════════════════════════════════════ */

export const PageSkeletonWrapper = styled.div`
  width: 100%;
`;

/** Full-width grey hero banner */
export const SkeletonHero = styled.div`
  width: 100%;
  height: 290px;
  background: #b0b0b0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
`;

/** Centred title area below hero */
export const SkeletonTitleSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 24px 32px;
  gap: 14px;
`;

/** Two-column content row */
export const SkeletonTwoCol = styled.div`
  display: flex;
  gap: 40px;
  max-width: 920px;
  margin: 0 auto;
  padding: 0 24px 60px;

  @media (max-width: 700px) {
    flex-direction: column;
  }
`;

export const SkeletonImageBox = styled.div`
  ${shimmerDark}
  flex: 0 0 340px;
  height: 220px;
  border-radius: 4px;

  @media (max-width: 700px) {
    flex: none;
    width: 100%;
  }
`;

export const SkeletonTextCol = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 4px;
`;

/* ═══════════════════════════════════════════
   FOOTER  (dark charcoal background)
═══════════════════════════════════════════ */

export const FooterSkeletonWrapper = styled.div`
  width: 100%;
  background: #5a5f65;
  padding: 32px 40px 24px;
  box-sizing: border-box;
`;

/** Top row: logo left, nav links right */
export const SkeletonFooterTopRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 40px;
  flex-wrap: wrap;
  margin-bottom: 20px;
`;

export const SkeletonFooterLogo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

export const SkeletonFooterNavLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px 28px;
  flex: 1;
  padding-top: 2px;
`;

/** Second nav row (catalogues) */
export const SkeletonFooterSecondRow = styled.div`
  display: flex;
  gap: 32px;
  flex-wrap: wrap;
  margin-bottom: 24px;
  padding-left: 2px;
`;

/** Divider */
export const SkeletonFooterDivider = styled.div`
  width: 100%;
  height: 1px;
  background: rgba(255, 255, 255, 0.18);
  margin-bottom: 18px;
`;

/** Bottom row: copyright left, social icons right */
export const SkeletonFooterBottomRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
`;

export const SkeletonSocialIcons = styled.div`
  display: flex;
  gap: 16px;
`;
